import { isApiError, unwrap, type ApiError } from "@sagot/api-client";
import { CONSOLE_PATHS, adminUrl, EMAIL_MAX_LENGTH, PASSWORD_MAX_LENGTH, PRODUCT_NAME, TURNSTILE_ACTIONS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Field, Icon, Input } from "@sagot/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { z } from "zod";
import { useErrorMessage } from "../lib/api-errors";
import { zodResolver } from "../lib/zod-resolver";
import { clearPendingAuth } from "../session/pending-auth";
import { useApi } from "../session/session-provider";
import { WORK_EMAIL_PLACEHOLDER } from "./auth-constants";
import { AuthDivider } from "./components/auth-divider";
import { AuthLayout } from "./components/auth-layout";
import { useCreateAccountPrompt } from "./components/auth-prompts";
import { FormAlert } from "./components/form-alert";
import { OAuthButton } from "./components/oauth-buttons";
import { PasswordInput } from "./components/password-input";
import { Turnstile } from "./components/turnstile";
import { SsoEntry } from "./sso-entry";
import { useCompleteSignIn } from "./use-sign-in";

function useLoginSchema() {
  const { t } = useTranslation();
  return z.object({
    email: z.string().trim().min(1, t("auth.common.emailRequired")).max(EMAIL_MAX_LENGTH, t("auth.common.emailTooLong")).email(t("auth.common.emailInvalid")),
    password: z.string().min(1, t("auth.common.passwordRequired")).max(PASSWORD_MAX_LENGTH, t("auth.common.passwordTooLong")),
    remember: z.boolean(),
  });
}

type LoginValues = z.infer<ReturnType<typeof useLoginSchema>>;

function LoginError({ error, onDismiss }: { error: ApiError; onDismiss: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const resetLink = (
    <AppLink href={CONSOLE_PATHS.forgotPassword} className="a-link font-semibold">
      {t("auth.login.resetLink")}
    </AppLink>
  );
  if (error.code === "account_locked") {
    return (
      <FormAlert title={t("auth.login.lockedTitle")} onDismiss={onDismiss}>
        {t("auth.login.lockedBody")} {resetLink}
      </FormAlert>
    );
  }
  if (error.code === "invalid_credentials") {
    const remaining = error.detailNumber("attempts_remaining");
    return (
      <FormAlert title={t("auth.login.errorTitle")} onDismiss={onDismiss}>
        {remaining !== undefined ? <>{t("auth.login.errorAttempts", { count: remaining })} </> : null}
        {resetLink}
      </FormAlert>
    );
  }
  return <FormAlert title={message(error)} onDismiss={onDismiss} />;
}

export function LoginPage() {
  const { t } = useTranslation();
  const api = useApi();
  const prompt = useCreateAccountPrompt();
  const [params] = useSearchParams();
  const completeSignIn = useCompleteSignIn();
  const [error, setError] = useState<ApiError | null>(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  useEffect(clearPendingAuth, []);
  const { register, handleSubmit, formState, getValues } = useForm<LoginValues>({
    resolver: zodResolver(useLoginSchema()),
    defaultValues: { email: "", password: "", remember: true },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      const body = await unwrap(api.POST("/v1/auth/login", { body: { ...values, captcha_token: captchaToken } }));
      await completeSignIn(body, { email: values.email, next: params.get("next") });
    } catch (caught) {
      if (!isApiError(caught)) throw caught;
      setError(caught);
      if (caught.detailFlag("captcha_required") || caught.code.startsWith("captcha_")) setCaptchaRequired(true);
      setCaptchaReset((n) => n + 1);
    }
  });

  // A wrong email/password pair marks both fields (they shake once, auth.css) without saying which one is wrong.
  const wrongCredentials = error?.code === "invalid_credentials";
  const passwordInvalid = Boolean(formState.errors.password) || wrongCredentials;
  return (
    <AuthLayout title={t("auth.login.title", { product: PRODUCT_NAME })} prompt={prompt}>
      <div className="a-region">
        <Icon name="globe" />
        {t("auth.login.yourData")} <span className="a-pill">{t("auth.aside.hosting")}</span>
      </div>
      <h1 className="a-title">{t("auth.login.heading")}</h1>
      <p className="a-sub">{t("auth.login.subtitle")}</p>
      {params.get("error") === "oauth" ? <FormAlert title={t("auth.login.oauthFailed")} /> : null}
      {error ? (
        <LoginError
          error={error}
          onDismiss={() => {
            setError(null);
          }}
        />
      ) : null}
      <OAuthButton provider="google" intent="login" variant="solid" label={t("auth.common.continueGoogle")} className="w-full" />
      <AuthDivider label={t("auth.common.or")} />
      <form noValidate onSubmit={(event) => void onSubmit(event)}>
        <Field className="a-fld" label={t("auth.common.workEmail")} error={formState.errors.email?.message}>
          <Input type="email" autoComplete="email" inputMode="email" placeholder={WORK_EMAIL_PLACEHOLDER} invalid={wrongCredentials || undefined} {...register("email")} />
        </Field>
        <div className="a-fld">
          <div className="a-lbl">
            <label className="label" htmlFor="login-password">
              {t("auth.common.password")}
            </label>
            <AppLink href={CONSOLE_PATHS.forgotPassword}>{t("auth.login.forgot")}</AppLink>
          </div>
          <PasswordInput
            id="login-password"
            autoComplete="current-password"
            placeholder={t("auth.common.passwordPlaceholder")}
            aria-invalid={passwordInvalid ? true : undefined}
            aria-describedby={formState.errors.password ? "login-password-error" : undefined}
            {...register("password")}
          />
          {formState.errors.password ? (
            <p id="login-password-error" role="alert">
              {formState.errors.password.message}
            </p>
          ) : null}
        </div>
        <div className="a-row">
          <label className="a-chk">
            <input type="checkbox" className="checkbox" {...register("remember")} />
            {t("auth.login.remember")}
          </label>
        </div>
        {captchaRequired ? <Turnstile action={TURNSTILE_ACTIONS.consoleLogin} onToken={setCaptchaToken} resetKey={captchaReset} /> : null}
        <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting} disabled={formState.isSubmitting || (captchaRequired && !captchaToken)}>
          {t("auth.login.submit")}
        </Button>
      </form>
      <div className="a-alt">
        <OAuthButton provider="microsoft" intent="login" label={t("auth.common.continueMicrosoft")} className="w-full" />
        <div className="a-sso">
          <SsoEntry key={String(error?.code === "sso_enforced")} email={getValues("email")} enforced={error?.code === "sso_enforced"} />
        </div>
      </div>
      <p className="a-foot">
        {t("auth.login.newOrg")}{" "}
        <AppLink href={CONSOLE_PATHS.register} className="a-link">
          {t("auth.login.createAccount")}
        </AppLink>
      </p>
      <div className="a-staff">
        <span>{t("auth.login.staffTitle")}</span>
        <a href={adminUrl("/login")}>{t("auth.login.staffBody")}</a>
      </div>
    </AuthLayout>
  );
}
